'use client';

import { fetchProductList } from '@/api/product';
import type { ListProduct } from '@/api/types/public';
import type { MarketingProductListResponse } from '../../types';
import { COLLABORATION_PRODUCT_LIST_REQUEST_OPTIONS } from './productRequestOptions';

export const COLLABORATION_PRODUCT_PAGE_SIZE = 20;

export interface CollaborationProductPageParams {
  groupId: number;
  page: number;
}

type CollaborationProductFetcher = (params: {
  append_correlation: 1;
  group_id: number;
  page: number;
  limit: number;
  in_stock: 3;
  stock_sort_first: 1;
}) => Promise<{
  code?: number;
  data?: MarketingProductListResponse;
}>;

export interface CollaborationProductPage {
  products: ListProduct[];
  total: number;
  hasMore: boolean;
}

export function getInitialCollaborationProductState({
  initialResponse,
  canLoad,
}: {
  initialResponse?: MarketingProductListResponse | null;
  canLoad: boolean;
}) {
  const products = (initialResponse?.list || initialResponse?.items || []) as ListProduct[];
  const total = Number(initialResponse?.total || initialResponse?.all_total || products.length);
  return {
    products,
    page: initialResponse ? 1 : 0,
    hasMore: initialResponse?.has_more ?? (initialResponse ? products.length < total : canLoad),
  };
}

export function mergeCollaborationProducts(
  current: ListProduct[],
  incoming: ListProduct[],
): ListProduct[] {
  const keys = new Set(
    current.map((product) => String(product.shop_sku || product.id || '')).filter(Boolean),
  );
  return incoming.reduce<ListProduct[]>(
    (result, product) => {
      const key = String(product.shop_sku || product.id || '');
      if (!key || keys.has(key)) return result;
      keys.add(key);
      result.push(product);
      return result;
    },
    [...current],
  );
}

export async function loadCollaborationProductPage(
  params: CollaborationProductPageParams,
  fetcher: CollaborationProductFetcher = fetchProductList,
): Promise<CollaborationProductPage> {
  const rawResponse = await fetcher({
    append_correlation: 1,
    group_id: params.groupId,
    page: params.page,
    limit: COLLABORATION_PRODUCT_PAGE_SIZE,
    ...COLLABORATION_PRODUCT_LIST_REQUEST_OPTIONS,
  });
  // 公共请求层会 resolve 业务失败响应；交给列表现有 catch 保留页码并提供重试。
  if (rawResponse?.code !== 200 || !rawResponse.data) {
    throw new Error('Failed to load collaboration product page');
  }
  const response = rawResponse.data;
  const products = (response.list || response.items || []) as ListProduct[];
  const total = Math.max(Number(response.total || response.all_total || products.length), 0);
  const hasMore =
    typeof response.has_more === 'boolean'
      ? response.has_more
      : params.page * COLLABORATION_PRODUCT_PAGE_SIZE < total;

  return { products, total, hasMore };
}
